import React from 'react';
import { Card, makeStyles } from '@fluentui/react-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Link as FluentLink } from '@fluentui/react-components';
import { toggleDialog } from '../../utils/store';
import { useDispatch } from 'react-redux';
import AccessInstructions from './AccessInstructions';
import AnalyticsChart from './AnalyticsChart';

const useStyles = makeStyles({
    // User message container — full styling since there's no outer wrapper
    userMessageContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '16px',
    },
    // Bot message container — minimal, outer Message.tsx handles layout
    botMessageContainer: {
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-start',
    },
    // User message card — needs all visual styling
    userMessageCard: {
        padding: '16px',
        maxWidth: '850px',
        outline: 'none',
        color: '#fff',
        opacity: 0.85,
        border: '0.5px solid rgba(255, 255, 255, 0.1)',
        backgroundColor: '#007738',
        boxShadow: '0 0 4px #00000032',
        borderRadius: '20px',
        '& p:last-child': {
            marginBottom: 0,
        },
    },
    // Bot message card — minimal, outer Message.tsx provides color/border/opacity/padding
    botMessageCard: {
        width: '100%',
        padding: '0',
        outline: 'none',
        backgroundColor: 'transparent',
        boxShadow: 'unset',
        '& p:last-child': {
            marginBottom: 0,
        },
    },
    markdownTableViewport: {
        width: '100%',
        overflowX: 'auto',
        marginTop: '16px',
        marginBottom: '16px',
    },
    markdownTable: {
        display: 'inline-table',
        width: 'auto',
        minWidth: 'min(560px, 100%)',
        borderCollapse: 'collapse',
    },
    markdownTableHeader: {
        minWidth: '110px',
        paddingTop: '6px',
        paddingRight: '24px',
        paddingBottom: '6px',
        paddingLeft: '0',
        whiteSpace: 'nowrap',
    },
    markdownTableCell: {
        minWidth: '110px',
        paddingTop: '4px',
        paddingRight: '24px',
        paddingBottom: '4px',
        paddingLeft: '0',
        whiteSpace: 'nowrap',
    },
});

interface TextMessageProps {
    message: string;
    isBot: boolean;
    hardcoded: boolean;
}

const TextMessage: React.FC<TextMessageProps> = ({ message, isBot, hardcoded }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    let processedMessage = message.replace(
        /\{\{(.+?)\}\}/g,
        (match, p1) => `[${p1}](#titleaction-${p1.split(' ').join('%20')})`
    );

    processedMessage = message.replace(
        /\{\{(.+?)\}\}/g,
        (match, p1) => `[${p1}](#productaction-${p1.split(' ').join('%20')})`
    );

    const handleProductLinkClick = (
        param: string,
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
        event.preventDefault(); // Prevent the default link behavior
        // Your action handling logic here
        console.log(`Action triggered with parameter: ${param}`);
        var searchOption = 'products'
        if (param.toLowerCase().includes("game%20pass") || param.toLowerCase().includes("gamepass")) {
             searchOption = 'subscriptions'
        }
        dispatch(
            toggleDialog({
                isOpen: true,
                searchString: param,
                searchTab: searchOption,
            })
        );
    };

    const handleTitleLinkClick = (
        param: string,
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
        event.preventDefault(); // Prevent the default link behavior
        // Your action handling logic here
        console.log(`Action triggered with parameter: ${param}`);
        dispatch(
            toggleDialog({
                isOpen: true,
                searchString: param,
                searchTab: 'title',
            })
        );
    };

    return (
        <div className={isBot ? classes.botMessageContainer : classes.userMessageContainer}>
            <Card className={isBot ? classes.botMessageCard : classes.userMessageCard}>
                <ReactMarkdown
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    components={{
                        //@ts-ignore
                        code: ({ node, className, children, inline, ...props }) => {
                            if (!inline && className === 'language-chart') {
                                try {
                                    const spec = JSON.parse(String(children).trim());
                                    return <AnalyticsChart spec={spec} />;
                                } catch {
                                    // If JSON parsing fails, render as normal code block
                                    return <code className={className} {...props}>{children}</code>;
                                }
                            }
                            return <code className={className} {...props}>{children}</code>;
                        },
                        //@ts-ignore
                        table: ({ node, children, ...props }) => (
                            <div className={classes.markdownTableViewport}>
                                <table className={classes.markdownTable} {...props}>
                                    {children}
                                </table>
                            </div>
                        ),
                        //@ts-ignore
                        th: ({ node, children, ...props }) => (
                            <th className={classes.markdownTableHeader} {...props}>
                                {children}
                            </th>
                        ),
                        //@ts-ignore
                        td: ({ node, children, ...props }) => (
                            <td className={classes.markdownTableCell} {...props}>
                                {children}
                            </td>
                        ),
                        //@ts-ignore
                        a: ({ node, children, href, title, ...props }) => {
                            if (href && href.startsWith('#titleaction-')) {
                                const param = href.substring(
                                    '#titleaction-'.length
                                );
                                return (
                                    //@ts-ignore
                                    <FluentLink
                                        onClick={(event) =>
                                            handleTitleLinkClick(param, event)
                                        }
                                        href={href}
                                        {...props}
                                    >
                                        {children}
                                    </FluentLink>
                                );
                            } else if (
                                href &&
                                href.startsWith('#productaction-')
                            ) {
                                const param = href.substring(
                                    '#productaction-'.length
                                );
                                return (
                                    //@ts-ignore
                                    <FluentLink
                                        onClick={(event) =>
                                            handleProductLinkClick(param, event)
                                        }
                                        href={href}
                                        {...props}
                                    >
                                        {children}
                                    </FluentLink>
                                );
                            }
                            // For regular links, just render the link as usual
                            return (
                                <a href={href} {...props}>
                                    {children}
                                </a>
                            );
                        },
                    }}
                >
                    {processedMessage}
                </ReactMarkdown>
            </Card>

        </div>
    );

    /*return (
        <div className={classes.messageContainer} style={{ justifyContent: isBot ? 'center' : 'flex-end' }}>
            <Card className={`${classes.messageCard}`} style={{ backgroundColor: isBot ? '#292C31' : '#007738', boxShadow: isBot ? 'unset' : '0 0 4px #00000032', padding: isBot ? '0' : '' }}>
                <Body2>{message.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                        {line}
                        <br />
                    </React.Fragment>
                ))}</Body2>
            </Card>
        </div>
    );*/
};

export default TextMessage;
